import { React, useEffect, useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box, CircularProgress, Paper, Button, Dialog,
    DialogActions, DialogContent, DialogTitle, Typography
} from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
    DataGridPro,
    GridToolbar
} from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';
import axios from 'axios';
import dayjs from 'dayjs';

import { socket } from '../services/socket';
import { formatCurrency } from '../utils/utils';

var api = axios.create({ withCredentials: true });

const BrokerInvoiceGrid = ({ lineItems, onUnapproveClick }) => {

    const rows = lineItems.map((invoice) => {
        const approval = invoice.approvalInfo;

        const isApproved = Boolean(
            approval?.approvalByAuth0Id &&
            approval?.approvalDate
        );

        return {
            id: invoice.lineItem.lineItemId,
            lineItemId: invoice.lineItem.lineItemId,
            status: isApproved ? 'APPROVED' : 'OPEN',
            tnInvoiceId: invoice.lineItem.invoiceSourceId,
            eventId: invoice.lineItem.eventSourceId,
            payDateWithinCycle: dayjs(invoice.lineItem.payOnDate).format('MM/DD/YYYY hh:mm A'),
            revenueSplit: invoice.lineItem.revenueSplit,
            profitSplit: invoice.lineItem.profitSplit,
            pastSplit: invoice.lineItem.pastSplit,
            totalSplit: invoice.lineItem.totalSplit,
            clawbackAmount: invoice.lineItem.clawbackAmount,
            clawbackReason: invoice.lineItem.clawbackReason,
            commissionPayedOn: dayjs(invoice.lineItem.lastPaymentDate).format('MM/DD/YYYY hh:mm A'),
            shippingTrackingStatus: invoice.lineItem.shippingTrackingStatus,
            ticketCost: invoice.lineItem.invoiceTicketCost,
            purchaseOrders: invoice.purchaseOrderInfo ?? [],
        };
    });

    const columns = [
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.15,
            renderCell: (params) => {
                const isApproved = params.value === 'APPROVED';

                return (
                    <span
                        style={{
                            color: isApproved ? '#2e7d32' : '#ed6c02',
                            fontWeight: 700
                        }}
                    >
                        {params.value}
                    </span>
                );
            }
        },
        { field: 'tnInvoiceId', headerName: 'Invoice ID', flex: 0.2, minWidth: 80 },
        {
            field: 'purchaseOrders',
            headerName: 'PO',
            flex: 0.5,
            minWidth: 400,
            sortable: true,
            filterable: false,
            valueGetter: (value, row) => {
                const purchaseOrders = row.purchaseOrders ?? [];

                const dates = purchaseOrders
                    .map(po => po.purchaseOrderSourceCreationDate)
                    .filter(Boolean)
                    .map(date => new Date(date).getTime())
                    .filter(timestamp => !Number.isNaN(timestamp));

                return dates.length > 0 ? Math.max(...dates) : null;
            },
            renderCell: (params) => {
                const purchaseOrders = params.row.purchaseOrders ?? [];

                if (purchaseOrders.length === 0) {
                    return '—';
                }

                return (
                    <Box sx={{ py: 1, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                        {purchaseOrders.map((po, index) => (
                            <div key={po.purchaseOrderId ?? index}>
                                {`Id: ${po.purchaseOrderSourceId ?? 'N/A'} | Date: ${
                                    po.purchaseOrderSourceCreationDate
                                        ? dayjs(po.purchaseOrderSourceCreationDate).format('MM/DD/YYYY')
                                        : 'N/A'
                                } | Balance: ${formatCurrency(
                                    po.purchaseOrderOpenCost ?? 0
                                )}`}
                            </div>
                        ))}
                    </Box>
                );
            },
        },
        { field: 'eventId', headerName: 'Event ID', flex: 0.2, minWidth: 80 },
        { field: 'payDateWithinCycle', headerName: 'Pay Date', flex: 0.2, minWidth: 120 },
        {
            field: 'ticketCost',
            headerName: 'Ticket Cost',
            flex: 0.15,
            renderCell: (p) => formatCurrency(p.value)
        },
        {
            field: 'revenueSplit',
            headerName: 'Revenue Split',
            flex: 0.15,
            renderCell: (p) => formatCurrency(p.value)
        },
        {
            field: 'profitSplit',
            headerName: 'Profit Split',
            flex: 0.15,
            renderCell: (p) => formatCurrency(p.value)
        },
        {
            field: 'pastSplit',
            headerName: 'Past Split',
            flex: 0.15,
            renderCell: (p) => formatCurrency(p.value)
        },
        {
            field: 'totalSplit',
            headerName: 'Payout',
            flex: 0.15,
            renderCell: (p) => formatCurrency(p.value)
        },
        {
            field: 'clawbackAmount',
            headerName: 'Clawback',
            flex: 0.15,
            renderCell: (p) => formatCurrency(p.value)
        },
        {
            field: 'clawbackReason',
            headerName: 'Clawback Reason',
            flex: 0.15
        },
        {
            field: 'commissionPayedOn',
            headerName: 'Paid On',
            flex: 0.2
        },
        {
            field: 'shippingTrackingStatus',
            headerName: 'Shipping Status',
            flex: 0.2
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.2,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const isApproved = params.row.status === 'APPROVED';

                return (
                    <Button
                        size="small"
                        variant="contained"
                        disabled={!isApproved}
                        onClick={() => onUnapproveClick(params.row)}
                        sx={{
                            backgroundColor: isApproved ? '#ed6c02' : '#bdbdbd',
                            color: '#fff',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: isApproved ? '#d65f00' : '#bdbdbd'
                            }
                        }}
                    >
                        UNAPPROVE
                    </Button>
                );
            }
        }
    ];

    return (
        <DataGridPro
            rows={rows}
            columns={columns}
            getRowHeight={() => 'auto'}
            hideFooter
            disableRowSelectionOnClick
            initialState={{
                sorting: {
                    sortModel: [{ field: 'status', sort: 'asc' }]
                }
            }}
        />
    );
};

const InvoiceDetailsGrid = ({ row, hasApprovedLineItems, onCloseCycleClick, onUnapproveClick }) => {
    const groupedByBroker = useMemo(() => {
        const map = new Map();

        (row.invoiceLineItems || []).forEach((item) => {
            const li = item?.lineItem;
            const approval = item?.approvalInfo;
            const brokerCompanyName = item?.brokerCompanyName;

            if (!li) return;

            const brokerName = brokerCompanyName;
            const brokerId = li.vendorId;
            const tnBrokerId = li.vendorSourceId;

            const isApproved = Boolean(
                approval?.approvalByAuth0Id &&
                approval?.approvalDate
            );

            const commissionValue = Number(li.totalSplit || 0);

            if (!map.has(brokerId)) {
                map.set(brokerId, {
                    id: brokerId,              
                    brokerId: tnBrokerId,
                    brokerName,
                    lineItems: [],
                    approvedCount: 0,
                    totalCount: 0,
                    approvedCommission: 0
                });
            }

            const broker = map.get(brokerId);

            broker.lineItems.push(item);
            broker.totalCount += 1;

            if (isApproved) {
                broker.approvedCount += 1;
                broker.approvedCommission += commissionValue;
            }
        });

        return Array.from(map.values());
    }, [row.invoiceLineItems]);

    const brokerColumns = [
        { field: 'brokerId', headerName: 'Broker ID', flex: 0.1 },
        { field: 'brokerName', headerName: 'Broker Name', flex: 0.6 },
        {
            field: 'approved',
            headerName: 'Approved',
            flex: 0.2,
            valueGetter: (_, row) =>
                `${row.approvedCount}/${row.totalCount}`
        },
        {
            field: 'approvedCommission',
            headerName: 'Approved Payout',
            flex: 0.2,
            renderCell: (params) => formatCurrency(params.value || 0)
        }
    ];

    const getDetailPanelContent = useCallback(
        ({ row }) => (
            <BrokerInvoiceGrid
                lineItems={row.lineItems}
                onUnapproveClick={onUnapproveClick}
            />
        ),
        [onUnapproveClick]
    );

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ height: 320 }}>
                <DataGridPro
                    rows={groupedByBroker}
                    getRowId={(row) => row.brokerId}
                    columns={brokerColumns}
                    hideFooter
                    disableRowSelectionOnClick
                    getDetailPanelHeight={() => 280}
                    getDetailPanelContent={getDetailPanelContent}
                    density="compact"
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    borderTop: '1px solid #e0e0e0',
                    pt: 1,
                    pr: 1
                }}
            >
                <Button
                    onClick={onCloseCycleClick}
                    disabled={!hasApprovedLineItems}
                    variant="contained"
                    sx={{
                        backgroundColor: hasApprovedLineItems
                            ? '#29bc35d1'
                            : '#bdbdbd',
                        color: hasApprovedLineItems
                            ? '#103cff'
                            : '#666',
                        fontWeight: 600,
                        '&:hover': {
                            backgroundColor: hasApprovedLineItems
                                ? '#C8E6C9'
                                : '#bdbdbd'
                        }
                    }}
                >
                    Close Cycle
                </Button>
            </Box>
        </Box>
    );
};

function CloseCycles() {
    const { maestroPermissions, isConnected } = useOutletContext();
    const { getAccessTokenSilently } = useAuth0();

    const [columns, setColumns] = useState(null);
    const [rows, setRows] = useState(null);
    const [closeCycleOpenDialog, setCloseCycleOpenDialog] = useState(false);
    const [selectedCycle, setSelectedCycle] = useState(null);
    const [unapproveDialogOpen, setUnapproveDialogOpen] = useState(false);
    const [selectedUnapproveRow, setSelectedUnapproveRow] = useState(null);
    const [data, setData] = useState([]);
    const [expandedRowIds, setExpandedRowIds] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const isAuthorized =
        maestroPermissions?.includes(`${process.env.REACT_APP_TENANT}:Commissions:Cycles:Read`) &&
        maestroPermissions?.includes(`${process.env.REACT_APP_TENANT}:Commissions:Cycles:Update`) &&
        maestroPermissions?.includes(`${process.env.REACT_APP_TENANT}:Commissions:LineItems:Read:Approval`) &&
        maestroPermissions?.includes(`${process.env.REACT_APP_TENANT}:Commissions:LineItems:Update`);

    const handleCloseCycleOpenDialog = (cycleRow) => {
        setSelectedCycle(cycleRow);
        setCloseCycleOpenDialog(true);
    };

    const handleUnapproveModalOpen = (row, lineItemRow) => {
        setSelectedCycle(row);
        setSelectedUnapproveRow(lineItemRow);
        setUnapproveDialogOpen(true);
    };

    const handleUnapproveModalSubmit = async () => {
        try {
            setIsLoading(true);
            
            const audience =
                process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;  
            
            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const lineItemIds = [ selectedUnapproveRow.lineItemId ];

            await api.post(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/${selectedCycle.cycleId}/open/lineItems/unapprove`,
                lineItemIds,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            const expanded = [...expandedRowIds];

            await getCommissionsApprovals();

            setExpandedRowIds(expanded);
            
            toast.success('Line item unapproved successfully');

        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                'Failed to unapprove line item'
            );
        } finally {
            setSelectedUnapproveRow(null);
            setIsLoading(false);
        } 
    };

    const handleCloseCycleSubmit = async () => {
        try {
            setIsLoading(true);

            const audience =
                process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience },
            });

            const lineItemIds =
                selectedCycle?.vendorsAndLineItems?.flatMap(vendor =>
                    (vendor.lineItems ?? [])
                        .filter(item =>
                            item?.approvalInfo?.approvalByAuth0Id &&
                            item?.approvalInfo?.approvalDate
                        )
                        .map(item => item.lineItem.lineItemId)
                ) ?? [];

            await api.post(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/${selectedCycle.cycleId}/close`,
                lineItemIds,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            toast.success('Cycle closed successfully');

            setCloseCycleOpenDialog(false);
            setSelectedCycle(null);

            getCommissionsApprovals();
        } catch (err) {
            toast.error(
                err?.response?.data?.message ||
                'Failed to close cycle'
            );
        } finally {
            setIsLoading(false);
        }
    };

    async function getCommissionsApprovals() {
        try {
            setIsLoading(true);

            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO
                },
            });

            const response = await api.get(
                `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/open/lineitems/approved/`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'x-tenant': process.env.REACT_APP_TENANT
                    }
                }
            );

            setData(
                response.data.map(item => ({
                    cycle: item.cycle,
                    vendors: item.vendorsAndLineItems
                }))
            );

        } catch (err) {
            toast.error(err?.response?.data?.message || 'Request failed');
        } finally {
            setIsLoading(false);
        }
    }

    const createRowsAndColumns = () => {
        const rows = data.map((item) => {
            const approvedPayout =
                item.vendors?.reduce((vendorTotal, vendor) => {
                    const approvedVendorTotal =
                        (vendor.lineItems ?? []).reduce((lineTotal, lineItem) => {
                            const isApproved =
                                lineItem?.approvalInfo?.approvalByAuth0Id &&
                                lineItem?.approvalInfo?.approvalDate;

                            return (
                                lineTotal +
                                (isApproved
                                    ? Number(lineItem?.lineItem?.totalSplit || 0)
                                    : 0)
                            );
                        }, 0);

                    return vendorTotal + approvedVendorTotal;
                }, 0) ?? 0;

            return {
                id: item.cycle.cycleId,
                cycleId: item.cycle.cycleId,
                commissionStartDate: dayjs(item.cycle.startDate).format('MM/DD/YYYY hh:mm A'),
                commissionEndDate: dayjs(item.cycle.endDate).format('MM/DD/YYYY hh:mm A'),
                approvedPayout,
                vendorsAndLineItems: item.vendors
            };
        });

        const columns = [
            { field: 'cycleId', headerName: 'Cycle ID', flex: 0.05 },
            { field: 'commissionStartDate', headerName: 'Period Start', flex: 0.1 },
            { field: 'commissionEndDate', headerName: 'Period End', flex: 0.1 },
            {
                field: 'approvedPayout',
                headerName: 'Approved Payout',
                flex: 0.15,
                renderCell: (params) => formatCurrency(params.value || 0)
            },
        ];

        setRows(rows);
        setColumns(columns);
    };

    const getDetailPanelContent = ({ row }) => {
        const hasApprovedLineItems =
            row?.vendorsAndLineItems?.some(vendor =>
                (vendor.lineItems ?? []).some(item =>
                    item?.approvalInfo?.approvalByAuth0Id &&
                    item?.approvalInfo?.approvalDate
                )
            ) ?? false;

        return (
            <InvoiceDetailsGrid
                row={{
                    invoiceLineItems:
                        row?.vendorsAndLineItems?.flatMap(v =>
                            (v.lineItems ?? []).map(li => ({
                                ...li,
                                brokerCompanyName: v.vendor?.companyName,
                            }))
                        ) ?? []
                }}
                hasApprovedLineItems={hasApprovedLineItems}
                onCloseCycleClick={() =>
                    handleCloseCycleOpenDialog(row)
                }
                onUnapproveClick={(lineItemRow) => handleUnapproveModalOpen(row, lineItemRow)}
            />
        );
    };

    const getDetailPanelHeight = useCallback(() => 377, []);

    useEffect(() => {
        if (isConnected && isAuthorized) {
            getCommissionsApprovals();
            socket.on('commissionsApprovals', getCommissionsApprovals);
        }

        return () => socket.off('commissionsApprovals');
    }, [isConnected]);

    useEffect(() => {
        if (data.length > 0) createRowsAndColumns();
        else {
            setRows([]);
            setColumns([]);
        }
    }, [data]);

    if (!isAuthorized) {
        return (
            <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
            >
                <WarningRoundedIcon size={60} thickness={4} />
                <p>Not Authorized</p>
            </Box>
        );
    }

    if ((!rows || !columns)) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper>
            <Dialog
                open={closeCycleOpenDialog}
                onClose={() => setCloseCycleOpenDialog(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    Close Cycle
                </DialogTitle>
                <DialogContent
                    sx={{
                        minHeight: 125,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}
                >
                    <Typography sx={{ fontSize: '13pt' }}>
                        Once SEND is clicked, invoices totaling{' '}
                        <Box
                            component="span"
                            sx={{
                                fontWeight: 'bold',
                                color: 'success.main'
                            }}
                        >
                            {formatCurrency(selectedCycle?.approvedPayout || 0)}
                        </Box>{' '}
                        will be sent to Tipalti for payment.
                        <br />
                        <Box
                            component="span"
                            sx={{
                                color: 'error.main',
                                fontWeight: 'bold'
                            }}
                        >
                            THIS ACTION CANNOT BE UNDONE.
                        </Box>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCloseCycleOpenDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCloseCycleSubmit}
                        sx={{
                            backgroundColor: '#29bc35d1',
                            color: '#103cff',
                            fontWeight: 600,
                            '&:hover': {
                                backgroundColor: '#C8E6C9'
                            }
                        }}
                    >
                        Close Cycle
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={unapproveDialogOpen}
                onClose={() => setUnapproveDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Unapprove Line Item
                </DialogTitle>

                <DialogContent
                    sx={{
                        minHeight: 125,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}
                >
                    <Box
                        sx={{
                            mb: 4,
                            display: 'inline-block',
                            textAlign: 'left'
                        }}
                    >
                        <Typography>
                            <strong>Cycle Start:</strong> {selectedCycle?.commissionStartDate}
                        </Typography>

                        <Typography>
                            <strong>Cycle End:</strong> {selectedCycle?.commissionEndDate}
                        </Typography>

                        <Typography>
                            <strong>Invoice ID:</strong> {selectedUnapproveRow?.tnInvoiceId}
                        </Typography>

                        <Typography>
                            <strong>Event ID:</strong> {selectedUnapproveRow?.eventId}
                        </Typography>
                    </Box>

                    <Typography sx={{ fontSize: '13pt' }}>
                        Are you sure you want to unapprove this line item?
                        <br />
                        <Box
                            component="span"
                            sx={{
                                color: 'error.main',
                                fontWeight: 'bold'
                            }}
                        >
                            THIS ACTION CANNOT BE UNDONE.
                        </Box>
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setUnapproveDialogOpen(false)}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="warning"
                        onClick={async () => {
                            await handleUnapproveModalSubmit(selectedUnapproveRow);

                            setUnapproveDialogOpen(false);
                            setSelectedUnapproveRow(null);
                        }}
                    >
                        Unapprove
                    </Button>
                </DialogActions>
            </Dialog>
            <Box sx={{ height: '85vh', width: '100%' }}>
                <DataGridPro
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 }
                        }
                    }}
                    loading={isLoading}
                    rows={rows}
                    columns={columns}
                    pagination
                    disableRowSelectionOnClick
                    getDetailPanelContent={getDetailPanelContent}
                    getDetailPanelHeight={getDetailPanelHeight}
                    detailPanelExpandedRowIds={expandedRowIds}
                    onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
                />
            </Box>
        </Paper>
    );
}

export default CloseCycles;